import type { MfgMaterialSdsDocument } from "@/lib/manufacturing/msdsDocumentTypes";
import type { MfgSdsSectionAudit } from "@/lib/manufacturing/msdsSections";
import { SDS_STATE_A_AI_DRAFT_NOTICE } from "@/components/manufacturing/pdf/sdsPdfFooterCopy";

type Props = {
  document: MfgMaterialSdsDocument | null;
  publishedDocument: MfgMaterialSdsDocument | null;
  sectionAudit: MfgSdsSectionAudit;
  dirty?: boolean;
};

function hasHumanSectionActivity(audit: MfgSdsSectionAudit): boolean {
  return Object.values(audit).some((e) => e && (e.status === "edited" || e.status === "opened"));
}

function latestActivity(audit: MfgSdsSectionAudit): { at: string; by: string } | null {
  let best: { at: string; by: string } | null = null;
  for (const e of Object.values(audit)) {
    if (!e || (e.status !== "edited" && e.status !== "opened")) continue;
    if (!best || e.last_touched_at > best.at) {
      best = { at: e.last_touched_at, by: e.last_touched_by };
    }
  }
  return best;
}

/** MSDS-4/5 — State A / pending / published banners. */
export function ManufacturingMaterialSdsBanner({
  document,
  publishedDocument,
  sectionAudit,
  dirty,
}: Props) {
  if (!document && publishedDocument) {
    const name = publishedDocument.review_signed_off_display_name.trim() || "Reviewer";
    const when = publishedDocument.review_signed_off_at
      ? new Date(publishedDocument.review_signed_off_at).toLocaleString()
      : "n/a";
    return (
      <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100">
        Published · reviewed by {name} on {when}
        {publishedDocument.source === "supplier_upload" ? " · supplier SDS" : ""}. Editing and
        saving creates a new draft (State A) until you sign off again.
      </div>
    );
  }

  const human = hasHumanSectionActivity(sectionAudit) || !!dirty;
  const activity = latestActivity(sectionAudit);

  if (human) {
    const when = activity?.at
      ? new Date(activity.at).toLocaleString()
      : dirty
        ? "just now (unsaved)"
        : "recently";
    const who = activity?.by ? activity.by.slice(0, 8) : "you";
    return (
      <div className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-[11px] text-slate-900 dark:border-slate-600 dark:bg-slate-900/50 dark:text-slate-100">
        Pending sign-off — last section activity by {who} at {when}. Publish requires your
        verification checkbox below.
      </div>
    );
  }

  if (document?.source === "supplier_upload") {
    return (
      <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] text-violet-950 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-100">
        Supplier SDS PDF attached — confirm it is the correct manufacturer/distributor sheet,
        then publish.
      </div>
    );
  }

  if (document?.source === "ai_draft") {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
        {SDS_STATE_A_AI_DRAFT_NOTICE}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
      Draft PDF pages use the locked disclaimer footer until a reviewer signs off and publishes.
    </div>
  );
}
